import {FC, useState, useEffect, useCallback, useMemo} from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	LinearProgress
} from "@mui/material";
import {
	DataGridPro,
	GridColDef,
	GridFilterModel,
	GRID_CHECKBOX_SELECTION_COL_DEF,
	GridValueGetterParams,
	GridToolbar
} from "@mui/x-data-grid-pro";
import {useListUsers, UserAttributeFilterDto, UserAttributeFilterDtoField, UserListDto, UsersControllerListParams, UsersControllerListSortBy} from "@/app2/shared/data";
import useCustomDataGrid from "@/app2/shared/hooks/useCustomDataGrid";
import useAddRecipientModal from "./hooks/useAddRecipientModal";

interface AddRecipientModalProps {
	open: boolean;
	onClose: () => void;
	onSave: (userIds: number[]) => void;
	existingRecipientUserIds: number[];
}

export const AddRecipientModal: FC<AddRecipientModalProps> = ({
	open,
	onClose,
	onSave,
	existingRecipientUserIds
}) => {

	const {
		constants: {
			allSelectedUserIds
		},
		states: {
			isLoading,
			isFetched,
			rows,
			rowCount,
			filterModel,
			selectedUserIds
		},
		api: {
			handleClose,
			handleSave,
			onPageChange,
			onPageSizeChange,
			onSortModelChange,
			onSelectionModelChange,
			onFilterChange
		}
	} = useAddRecipientModal({
		open,
		existingRecipientUserIds,
		onClose,
		onSave
	});

	const {
		api: {
			assignFilters
	}} = useCustomDataGrid<UserListDto>({});

	const data = useMemo(() =>
		isFetched && !isLoading ? rows : [],
	[isFetched, isLoading, rows]);	

	const columns: GridColDef<UserListDto>[] = [
		{
			...GRID_CHECKBOX_SELECTION_COL_DEF,
			type: "string",
			hideable: false
		},
		{
			field: "name",
			type: "string",
			flex: 1,
			headerName: "Name",
			filterable: false,
			valueGetter: (params: GridValueGetterParams<UserListDto>) => {
				const u = params.row;
				return `${u.firstname || ""} ${u.lastname || ""}`.trim() || (u.email as string) || "";
			}
		},
		{
			field: "email",
			type: "string",
			flex: 1,
			headerName: "Email"
		},
		{
			field: "employeeCode",
			type: "string",
			flex: 1,
			headerName: "Employee Code"
		},
		{
			field: "jobTitle",
			type: "string",
			flex: 1,
			headerName: "Job Title"
		},
		{
			field: "division",
			type: "string",
			flex: 1,
			headerName: "Division"
		}
	];

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="lg"
			fullWidth
			PaperProps={{
				sx: {
					backgroundColor: "background.paperElevation2",
					borderRadius: "4px"
				}
			}}
		>
			<DialogTitle>Add Recipients</DialogTitle>
			<DialogContent>
				<Box
					sx={{
						height: "60vh",
						mt: 2,
						"& .MuiDataGrid-toolbarContainer button[aria-label='Export']": {display: "none !important"},
						"& .MuiDataGrid-row.recipient-disabled": {
							opacity: 0.5,
							cursor: "not-allowed",
							"& .MuiDataGrid-cell": {color: "text.disabled"},
							"& .MuiDataGrid-cellCheckbox .MuiCheckbox-root": {
								opacity: 0.5,
								cursor: "not-allowed",
								pointerEvents: "none",
								"& svg": {color: "text.disabled", "& path": {fill: "text.disabled"}}
							}
						}
					}}
				>
					<DataGridPro<UserListDto>
						autoHeight
						loading={isLoading}
						rows={data}
						columns={columns.map(e => assignFilters(e as any))}
						rowCount={rowCount}
						page={(filterModel.page ?? 1) - 1}
						pageSize={filterModel.pageSize ?? 25}
						onPageChange={onPageChange}
						onPageSizeChange={onPageSizeChange}
						pagination
						paginationMode="server"
						sortingMode="server"
						onSortModelChange={onSortModelChange}
						filterMode="server"
						onFilterModelChange={onFilterChange}
						checkboxSelection
						selectionModel={allSelectedUserIds}
						onSelectionModelChange={(m) => onSelectionModelChange(m as number[])}
						keepNonExistentRowsSelected
						isRowSelectable={(params) => !existingRecipientUserIds.includes(params.row.id)}
						getRowClassName={(params) =>
							existingRecipientUserIds.includes(params.id as number) ? "recipient-disabled" : ""
						}
						getRowId={(row) => row.id}
						rowsPerPageOptions={[25, 50, 100, 200]}
						components={{Toolbar: GridToolbar}}
						initialState={{
							pagination: {pageSize: 25},
							sorting: {sortModel: [{field: "lastname", sort: "asc"}]}
						}}
					/>
				</Box>
				{isLoading && <LinearProgress />}
			</DialogContent>
			<DialogActions sx={{p: 2}}>
				<Button onClick={handleClose} variant="text" color="primary">
					Cancel
				</Button>
				<Button
					onClick={handleSave}
					variant="text"
					color="primary"
					disabled={selectedUserIds.length === 0}
				>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};
